/**
 * finixt-ui's Toggle with its label beside it, named by reference.
 *
 * finixt-ui wraps the switch and its label in a `<label>`, and a label
 * forwards a click to the button inside it, so one click toggled twice and
 * the switch never moved. The text here is a span the switch points at with
 * `aria-labelledby`; clicking the text toggles once, as a label would. The
 * button is also typed, so a switch inside a form does not submit it.
 */
import { useId } from 'react';

import { Toggle as FinixtToggle, type ToggleProps as FinixtToggleProps } from './finixt.js';

export interface ToggleProps extends Omit<FinixtToggleProps, 'label' | 'onChange'> {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: string | undefined;
}

export function Toggle({ checked, onChange, label, disabled, ...rest }: ToggleProps) {
  const id = useId();
  const toggle = (
    <FinixtToggle
      type="button"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      aria-labelledby={label ? id : undefined}
      {...rest}
    />
  );
  if (!label) return toggle;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
      {toggle}
      <span
        id={id}
        onClick={() => !disabled && onChange(!checked)}
        style={{
          fontSize: 'var(--text-body-sm)',
          color: 'var(--text-muted)',
          fontWeight: 'var(--weight-medium)',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        {label}
      </span>
    </span>
  );
}
